<template>
  <div v-show="isOpening">

    <div class="mobile-booking-widget-backdrop" @click="hide"></div>

    <div class="mobile-booking-widget bg-white">
      <div class="dp-mobile-container h-16 flex mdsm:w-full sm:flex-none">
        <div class="flex items-center w-full">

          <template v-if="step === 1">
            <div class="flex-1 font-medium">
              <div class="text-black-light-5" v-html="dateDisplayValue"></div>
            </div>
          </template>
          <template v-else>
            <input type="text"
              class="flex-1 mobile-booking-widget-header font-medium"
              readonly
              :value="displayValue"
              :placeholder="titlePlaceholder" />
          </template>

          <button type="button" class="flex-none" @click="hide">
            <svg width="24" height="24">
              <use xlink:href="/img/sprite.svg#ic_close"></use>
            </svg>
          </button>
        </div>
      </div>

      <hr class="border-black-30" />
      <template v-if="step === 1">
        <div class="dp-mobile-container py-5 dp-mobile-scroll-container overflow-x-hidden overflow-y-auto">
          <div v-for="(month, index) in months" :key="index" class="pb-8 px-3 -mx-3 overflow-hidden">
            <calendar-header :month="month" />
            <calendar-grid
              :month="month"
              :from="value.date[0]"
              :to="value.date[1]"
              @click="handleDateClicked" />
          </div>
        </div>
      </template>
      <template v-else-if="step === 2">
        <detailed-guest-picker v-model="value.guests" class="dp-mobile-container py-5 mdsm:w-full" />
      </template>
      <template v-else-if="step === 3">
        <room-picker v-model="value.rooms" class="dp-mobile-container py-5 mdsm:w-full" />
      </template>

      <hr class="border-black-30" />
      <div class="dp-mobile-container flex-none h-20 flex mdsm:w-full">
        <div class="flex m-auto w-full">
          <div class="flex-1 text-left">
            <button type="button" class="btn btn-outlined btn-tertiary input-tall"
              @click="handleCancelFlow">
              Cancel
            </button>
          </div>
          
          <div class="flex-none">
            <button type="button"
              class="btn btn-secondary input-tall"
              :disabled="!canProceed"
              @click="goToStep(step + 1)">
              <span v-if="step === 1">Select dates</span>
              <span v-else-if="step === 2">Add guests</span>
              <span v-else-if="step === 3">Add rooms</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CalendarHeader from '../DateRangePicker/CalendarHeader'
import CalendarGrid from '../DateRangePicker/CalendarGrid'
import DetailedGuestPicker from '../Booking/DetailedGuestPicker'
import RoomPicker from '../Booking/RoomPicker'
import {
  determineBestDPRange,
  getBookingDateDisplay,
  getDetailedBookingGuestsDisplay,
  getBookingRoomsDisplay,
} from '../../../helpers'

export default {
  components: {
    CalendarHeader,
    CalendarGrid,
    DetailedGuestPicker,
    RoomPicker
  },
  props: ['value'],
  data () {
    const [from, to] = this.value.date

    return {
      isOpening: false,
      step: 1,
      displayingMonth: from ? from : moment()
    }
  },
  computed: {
    titlePlaceholder () {
      switch (this.step) {
        case 1: return 'Add travel dates'
        case 2: return 'Add guests'
        case 3: return 'Add rooms'
        default: return ''
      }
    },
    displayValue () {
      switch (this.step) {
        case 1: return this.dateDisplayValue
        case 2: return getDetailedBookingGuestsDisplay(this.value.guests)
        case 3: return getBookingRoomsDisplay(this.value.rooms)
        default: return ''
      }
    },
    dateDisplayValue () {
      const [from, to] = this.value.date

      const [formattedFrom, formattedTo] = getBookingDateDisplay(from, to)

      if (from && !to) {
        return `<span class="text-black-100">${formattedFrom}</span> — Check-out`
      }

      if (from && to) {
        return `<span class="text-black-100">${formattedFrom} — ${formattedTo}</span>`
      }

      return 'Add travel dates'
    },
    canProceed () {
      switch (this.step) {
        case 1: return moment.isMoment(this.value.date[0]) && moment.isMoment(this.value.date[1])
        case 2: return Boolean(this.value.guests.divers) ||
                       Boolean(this.value.guests.studentDivers) ||
                       Boolean(this.value.guests.nonDivers)
        case 3: return Boolean(this.value.rooms)
        default: return false
      }
    },
    months () {
      let cursor = moment().startOf('month')
      const numberOfMonths = 25
      const months = []

      while (months.length < numberOfMonths) {
        months.push(cursor)

        cursor = moment().add(months.length, 'months')
      }

      return months
    }
  },
  methods: {
    show (step) {
      this.isOpening = true
      this.step = step
    },
    hide () {
      this.isOpening = false
    },
    handleDateClicked (date) {
      const [from, to] = determineBestDPRange(this.from, this.to, date)

      this.from = from
      this.to = to
      const formData = this.value
      formData.date = [from, to]
      this.$emit('input', formData)
    },
    goToStep (nextStep) {
      this.step = nextStep

      if (this.step === 4) {
        this.isOpening = false
      }
    },
    handleCancelFlow () {
      this.$emit('input', {
        date: [null, null],
        guests: {
          divers: 0,
          studentDivers: 0,
          nonDivers: 0
        },
        rooms: 0
      })

      this.isOpening = false
    }
  },
  watch: {
    isOpening (value) {
      if (value) {
        window.toggleTrengoChat({ show: false })
      }
      else {
        window.toggleTrengoChat({ show: true })
      }
    }
  }
}
</script>
